import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SelectOption } from '@kula-ai/components';
import {
  fetchUserSearch,
  searchedUserName,
  type SearchedUser,
} from './fetchUserSearch';

/**
 * Surfaces key their recipients either by user id or by email — the reject
 * flows match the sender on `email`, so an id-keyed option would silently
 * write the wrong shape into their payload.
 */
export type UserOptionKey = 'id' | 'email';

export const toCcBccOptionBy =
  (key: UserOptionKey) =>
  (user: SearchedUser): SelectOption | null => {
    if (key === 'email' && !user.email) return null;
    const label = searchedUserName(user);
    return {
      value: key === 'email' ? String(user.email) : String(user.id),
      label,
      description: label === user.email ? undefined : (user.email ?? undefined),
      image: user.avatar_thumb_url ?? undefined,
    };
  };

/** Maps a search page, dropping entries that cannot satisfy the key. */
export const toCcBccOptionsBy = (
  users: SearchedUser[],
  key: UserOptionKey
): SelectOption[] =>
  users.map(toCcBccOptionBy(key)).filter((o): o is SelectOption => o !== null);

export const toCcBccOption = (user: SearchedUser): SelectOption => {
  const label = searchedUserName(user);
  return {
    value: String(user.id),
    label,
    // Only when the label is a name — otherwise it already IS the email.
    description: label === user.email ? undefined : (user.email ?? undefined),
    image: user.avatar_thumb_url ?? undefined,
  };
};

/** Saved `cc_participants` / `bcc_participants` rows → options (ENG-3766). */
export const participantsToOptions = (
  participants: unknown
): SelectOption[] => {
  if (!Array.isArray(participants)) return [];
  return participants
    .filter(
      (p): p is Record<string, unknown> =>
        typeof p === 'object' &&
        p !== null &&
        p.id !== undefined &&
        p.id !== null
    )
    .map((p) => {
      // The participant partial emits full_name / first_name / last_name and
      // avatar_url — not `name` / `avatar`.
      const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
      const name =
        str(p.full_name) ||
        `${str(p.first_name)} ${str(p.last_name)}`.trim() ||
        str(p.name);
      const email = typeof p.email === 'string' ? p.email : undefined;
      return {
        value: String(p.id),
        label: name || email || `User ${p.id}`,
        description: name ? email : undefined,
        image: str(p.avatar_url) || str(p.avatar) || undefined,
      };
    });
};

/** Concatenates user lists, keeping the first entry per id. */
const mergeUsersById = (...lists: SearchedUser[][]): SearchedUser[] => {
  const byId = new Map<string, SearchedUser>();
  lists.flat().forEach((user) => {
    const key = String(user.id);
    if (!byId.has(key)) byId.set(key, user);
  });
  // `Array.from`, not a spread: the build targets es5, where iterating a Map
  // needs `downlevelIteration`.
  return Array.from(byId.values());
};

/** Concatenates option lists, keeping the first entry per value. */
export const mergeOptionsByValue = (
  ...lists: SelectOption[][]
): SelectOption[] => {
  const byValue = new Map<string, SelectOption>();
  lists.flat().forEach((option) => {
    const key = String(option.value);
    if (!byValue.has(key)) byValue.set(key, option);
  });
  return [...byValue.values()];
};

export const ccBccUserSearchQueryKey = (query: string) =>
  ['account-users-search', query] as const;

/**
 * CC/BCC options: every active user, searched server-side.
 *
 * Being copied needs no send permission, so this is everyone — not the
 * delegatable senders these surfaces used to reuse (ENG-3766).
 */
export function useCcBccUserOptions(
  knownOptions: SelectOption[] = [],
  valueKey: UserOptionKey = 'id'
) {
  const queryClient = useQueryClient();
  // Everyone the pickers have seen. The menu only holds the last search, so
  // callers mapping a selected id back to an email need this.
  const seenRef = useRef(new Map<string, SelectOption>());

  const register = useCallback((options: SelectOption[]) => {
    options.forEach((option) => {
      seenRef.current.set(String(option.value), option);
    });
    return options;
  }, []);

  const { data: firstPage = [] } = useQuery({
    queryKey: ccBccUserSearchQueryKey(''),
    queryFn: ({ signal }) => fetchUserSearch({ signal }),
    staleTime: 60_000,
    select: (users) => toCcBccOptionsBy(users, valueKey),
  });

  // Saved recipients first: a chip outside the first page has no other way to
  // resolve its label on a fresh load.
  const initialOptions = useMemo(
    () => register(mergeOptionsByValue(knownOptions, firstPage)),
    [knownOptions, firstPage, register]
  );

  const loadOptions = useCallback(
    async (query: string): Promise<SelectOption[]> => {
      const term = query?.trim() ?? '';
      // Cached so CC and BCC share one request per term.
      const users = await queryClient.fetchQuery({
        queryKey: ccBccUserSearchQueryKey(term),
        queryFn: ({ signal }) => fetchUserSearch({ query: term, signal }),
        staleTime: 60_000,
      });
      return register(toCcBccOptionsBy(users, valueKey));
    },
    [queryClient, register, valueKey]
  );

  /** Email for a selected value, from anything the pickers have seen. */
  const getEmailByValue = useCallback((value: string): string => {
    const description = seenRef.current.get(String(value))?.description;
    return typeof description === 'string' ? description : '';
  }, []);

  return { initialOptions, loadOptions, getEmailByValue };
}

/** Puts a kula `Select` into async (searched) mode. */
export const asyncSelectProps = (
  loadOptions: (_query: string) => Promise<SelectOption[]>
) => ({
  variant: 'async' as const,
  // Opens on the first page rather than an empty menu.
  defaultOptions: true,
  loadOptions,
});

const matchesQuery = (option: SelectOption, query: string) => {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  const label = String(option.label ?? '').toLowerCase();
  const description = String(option.description ?? '').toLowerCase();
  return label.includes(term) || description.includes(term);
};

export const SENDER_SEARCH_PAGE_SIZE = 30;

/**
 * From options: the same user search as CC/BCC, plus the surface's own rows.
 *
 * `baseOptions` (senders, shared mailboxes, organizer sentinel) is what the
 * field opens on. Shared mailboxes and the sentinel are matched locally — the
 * user search does not return them.
 */
export const useSenderAutocomplete = (
  baseOptions: SelectOption[],
  valueKey: UserOptionKey = 'id'
) => {
  const queryClient = useQueryClient();
  // Senders the search returned. A payload mapper resolves the pick back to a
  // user object, and it cannot find one the local list never held.
  const [searchedSenders, setSearchedSenders] = useState<SearchedUser[]>([]);

  const loadOptions = useCallback(
    async (query: string): Promise<SelectOption[]> => {
      const term = query?.trim() ?? '';
      if (!term) return baseOptions;

      // A user option is keyed the way this surface keys users; anything else
      // is a shared mailbox or the organizer sentinel.
      const isUserOption = (option: SelectOption) =>
        valueKey === 'email'
          ? String(option.value).includes('@')
          : /^\d+$/.test(String(option.value));
      const knownUsers = new Map(
        baseOptions
          .filter(isUserOption)
          .map((option) => [String(option.value), option])
      );

      // Asks for delegatable senders only. The endpoint does not honour it —
      // see the note on `emailDelegatableOnly` — so the page it answers with
      // is every active user, and the intersection below is what actually
      // narrows it.
      const users = await queryClient.fetchQuery({
        queryKey: ['sender-users-search', term, valueKey] as const,
        queryFn: ({ signal }) =>
          fetchUserSearch({
            query: term,
            items: SENDER_SEARCH_PAGE_SIZE,
            emailDelegatableOnly: true,
            signal,
          }),
        staleTime: 60_000,
      });

      // The search is here for recall, not for reach: it matches mid-word and
      // through misspellings, which a local substring filter cannot (ENG-3766).
      // What it may offer is still only what this surface already lists —
      // `baseOptions` is the account's delegatable senders (a complete list,
      // scoped server-side to connected mailboxes) plus its shared mailboxes
      // and whoever is already saved on the record. Anyone else has no mailbox
      // to send from, and picking them failed the save with "Sender's mailbox
      // is not connected" only after the whole email had been written
      // (ENG-3962).
      //
      // Mapped in the surface's own key — an id-keyed option on an
      // email-keyed surface writes the wrong shape into its payload.
      const searched = users
        .map((user) => ({ user, option: toCcBccOptionBy(valueKey)(user) }))
        .filter(
          (row): row is { user: SearchedUser; option: SelectOption } =>
            row.option !== null && knownUsers.has(String(row.option.value))
        );

      setSearchedSenders((prev) =>
        mergeUsersById(
          prev,
          searched.map((row) => row.user)
        )
      );

      const matchedUsers = searched.map(
        (row) => knownUsers.get(String(row.option.value)) ?? row.option
      );

      // Delegates the search cannot return — it filters to active users and
      // pages at 30 — would otherwise drop off the list the moment you type.
      const matchedDelegates = baseOptions
        .filter(isUserOption)
        .filter((option) => matchesQuery(option, term));

      const matchedNonUsers = baseOptions
        .filter((option) => !isUserOption(option))
        .filter((option) => matchesQuery(option, term));

      return mergeOptionsByValue(
        matchedUsers,
        matchedDelegates,
        matchedNonUsers
      );
    },
    [baseOptions, queryClient, valueKey]
  );

  const fromSelectProps = useMemo(
    () => asyncSelectProps(loadOptions),
    [loadOptions]
  );

  return { fromSelectProps, searchedSenders };
};

export const useSenderAutocompleteProps = (baseOptions: SelectOption[]) =>
  useSenderAutocomplete(baseOptions).fromSelectProps;
